import { useEffect, useState, useCallback } from "react";
import { UpdateFormCardUI } from "@app/pages/Features/GroupList/GroupDetail/InspectionTab/FormUpdateCard";
import {
  DataTable,
  DataDialog,
  DataPagination,
  Button as FormActionButton,
} from "@app/components/atoms";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  TextField,
  Box,
  debounce,
  Switch,
  Divider,
  Checkbox,
} from "@mui/material";
import {
  IconPlus,
  IconActionOverflow,
  IconDeleteRow,
  IconEdit,
  IconWarningFormEdit,
  IconUser,
  IconInputError,
  IconInvalidUrl,
  IconDelete,
  IconBox,
} from "@app/components/icons";

import { IconPublishedRow } from "@app/components/icons/IconPublishedRow/IconPublishedRow";
import {
  ConfirmDialog,
  ConfirmDialogProps,
  CreateInspectionForm,
  InspectionFormDialog,
} from "@app/pages/Features";
import {
  REGEX,
  isMobile,
  resetIsContinuousLoading,
  safeSliceGraphemes,
  scrollToTop,
  setItem,
} from "@app/helpers";
import {
  CURRENT_PAGE,
  DEBOUNCE_TIME,
  DialogTypes,
  INVALID_URL,
  LIMIT_PAGE,
  StorageKey,
  TOTAL_PAGE,
  TYPE_FILE_UPLOAD,
  ResultType,
} from "@app/constants";
import {
  apiGroupDetailInspectionFormDelete,
  apiGroupDetailInspectionFormStatus,
  apiGroupDetailInspectionForms,
  getCustomInspectionFormTemplates,
  updateInspectionFormAndItemsApiCallWeb,
  getUser,
  apiGetusergroups,
  updateInspectionFormStatusApiCallNew,
  apiGroupMachineTypeWebNew,
  apiGetInspectionItemsNew,
  getInspectionItemsNew,
  apiPostInspectionFormMultipleGroupsMachinesNew,
  apiGetBlankInspectionFormTemplate,
} from "@app/api";
import { format } from "date-fns";
import {
  store,
  setShowAlert,
  setCurrentInspectionInfo,
  RootState,
  setInspectionsTemplate,
  setMachineId,
  setIsEditDraft,
} from "@app/store";
import { useSelector } from "react-redux";
import { UploadFileType } from "@app/constants";
import { handleUploadStore } from "@app/helpers";
import {
  Add,
  DownloadOutlined,
  MoreVert,
  UploadOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import React from "react";
import { Search, X } from "lucide-react";
import { PreviewInspectionDialogEdit } from "../Components/InspectionForm/PreviewInspectionDialogEdit";
import "@app/pages/Features/GroupList/GroupDetail/InspectionTab/InspectionTab.css";
import { MediaUpload } from "../Components/InspectionForm/MediaUpload/MediaUpload";
import { InspectionTemplateSelection } from "@app/types";
import {
  buildInspectionBulkTemplateFileName,
  buildInspectionBulkTemplateWorkbookBuffer,
  buildInspectionTemplateOptionColumnHeaders,
  downloadInspectionBulkTemplateWorkbook,
  limitInspectionFormItems,
  MAX_INSPECTION_FORM_ITEMS,
} from "@app/helpers/inspection-bulk-template.helper";

enum INSPECTION_TYPE {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  ARCHIVED = "ARCHIVED",
}

type InspectionType = {
  id: number;
  currentStatus?: string;
  givenName?: string;
  inspectionFormName?: string;
  lastStatusUpdatedAt?: string;
  machineId?: string;
  machineName?: string;
  machinePictureUrl?: string;
  machineTypePictureUrl?: string;
  surname?: string;
  userPictureUrl?: string;
};

const INSPECTION_STATUS = {
  [INSPECTION_TYPE.DRAFT]: "draft",
  [INSPECTION_TYPE.PUBLISHED]: "published",
  [INSPECTION_TYPE.ARCHIVED]: "archived",
};

const dataInspectionFormStatus = [
  {
    label: "all",
    key: "inspectionFormCount",
    inspectionStatus: "",
    count: 0,
  },
  {
    label: "filterInspection.published",
    key: "publishedStatusCount",
    inspectionStatus: "PUBLISHED",
    count: 0,
  },
  {
    label: "filterInspection.draft",
    key: "draftStatusCount",
    inspectionStatus: "DRAFT",
    count: 0,
  },
];

const resolveBlankInspectionTemplateFileUrl = (response: any) => {
  const data = response?.data?.data;

  if (typeof data === "string") {
    return data;
  }

  return (
    data?.blankInspectionFormTemplateFileUrl ??
    data?.BlankInspectionFormTemplateFileUrl ??
    data?.blankInspectionFormTemplatePathUrl ??
    data?.inspectionFormTemplateFileUrl ??
    data?.templateFileUrl ??
    data?.fileUrl
  );
};

const getInspectionTemplateName = (
  selectedTemplate: InspectionTemplateSelection["selectedTemplate"],
) => {
  if ("inspectionFormTemplateName" in selectedTemplate) {
    return selectedTemplate.inspectionFormTemplateName;
  }

  return selectedTemplate.name;
};

type InspectionItem = {
  inspectionItemId: string;
  name: string;
  description: string;
  isRequired?: boolean;
  position?: number;
  inspectionItemMedias: {
    fileName: string;
    filePath: string;
    mediaUrl: string;
    mimeType: string;
  }[];
  status?: string;
  resultType?: string;
  options?: {
    name: string;
    position?: number;
    customInspectionItemsOptionsId?: string;
  }[];
};

const hasValidPredefinedComments = (item: InspectionItem) => {
  if (item.resultType !== ResultType.FREE_TEXT) {
    return true;
  }

  return (
    Array.isArray(item.options) &&
    item.options.length > 0 &&
    item.options.every((option) => !!option?.name?.trim())
  );
};

const buildInspectionItemOptionsPayload = (item: InspectionItem) => {
  if (item.resultType !== ResultType.FREE_TEXT || !item.options?.length) {
    return {};
  }

  return {
    options: item.options.map((option, optionIndex) => ({
      ...(option.customInspectionItemsOptionsId
        ? {
            customInspectionItemsOptionsId:
              option.customInspectionItemsOptionsId,
          }
        : {}),
      name: option.name,
      position: optionIndex + 1,
    })),
  };
};

type AssignmentMachine = {
  machineId: string;
  groupId: string;
  pictureUrl: string;
  machineName: string;
  modelAndType: string;
  machineManufacturerName: string;
  customMachineManufacturerName: string;
  customTypeName: string | null;
  serialNumber: string;
  machineTypeId: string;
  typeName: string;
  groupName: string;
  isAssigned: boolean;
};

const getMachineTypeDisplayName = (
  machine: Pick<AssignmentMachine, "customTypeName" | "typeName">,
) => machine.customTypeName || machine.typeName;

const getMachineTypeKey = (
  machine: Pick<
    AssignmentMachine,
    "machineTypeId" | "customTypeName" | "typeName"
  >,
) => {
  if (machine.customTypeName) {
    return `${machine.machineTypeId}::${machine.customTypeName}`;
  }
  return machine.machineTypeId;
};

export function InspectionTab() {
  const [inspectionItems, setInspectionItems] = useState<InspectionItem[]>([]);
  const [validationStates, setValidationStates] = useState<boolean[]>([]);
  const [validationStatesDesc, setValidationStatesDesc] = useState<boolean[]>(
    [],
  );
  const [inspectionFormId, setInspectionFormId] = useState<string>("");
  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [openStates, setOpenStates] = useState<boolean[]>([]);
  const { t } = useTranslation();
  const [openInspectionsForm, setOpenInspectionsForm] = useState(false);
  const { id: groupId } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bulkAnchorEl, setBulkAnchorEl] = useState<null | HTMLElement>(null);
  const [targetRow, setTargetRow] = useState<any>(null);
  const [inspectionStatus, setInspectionStatus] = useState<
    INSPECTION_TYPE | ""
  >("");
  const [open, setOpen] = useState(false);
  const [openFormDelete, setOpenFormDelete] = useState(false);
  const [openFormDeleteAssignment, setOpenFormDeleteAssignment] =
    useState(false);
  const [openAddNewItem, setOpenAddNewItem] = useState(false);
  const [openFormArchive, setOpenFormArchive] = useState(false);
  const [openFormPublish, setOpenFormPublish] = useState(false);
  const [openFormName, setOpenFormName] = useState(false);
  const [openBulkTemplateDownload, setOpenBulkTemplateDownload] =
    useState(false);
  const [openBulkTemplateUpload, setOpenBulkTemplateUpload] = useState(false);
  const openFormAction = Boolean(anchorEl);
  const openBulkAction = Boolean(bulkAnchorEl);
  const [dataSource, setDataSource] = useState<any[]>();
  const [order, setOrder] = useState<string | undefined>();
  const [field, setField] = useState<string>();
  const [requestData, setRequestData] = useState(new Date());
  const [openDialogConfirm, setOpenDialogConfirm] = useState(false);
  const [openAssignmentForm, setOpenAssignmentForm] = useState(false);
  const [openConAssignmentForm, setOpenConAssignmentForm] = useState(false);
  const [openConAssignmentFormMin, setOpenConAssignmentFormMin] =
    useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>({});
  const [isInit, setIsInit] = useState(true);
  const [, setDeletedItemIds] = useState<string[]>([]);
  const [serviceMeterName, setServiceMeterName] = useState<string[]>([]);
  const [odoMeterName, setOdoMeterName] = useState<string[]>([]);
  const [odoMeterIsRequired, setOdoMeterIsRequired] = useState<boolean>(false);
  const [assigned, setAssigned] = useState<boolean>(false);
  const [odoMeterID, setOdoMeterID] = useState<string[]>([]);
  const [removedMediaStates, setRemovedMediaStates] = useState<
    Record<string, string[]>
  >({});
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);

  const [mediaStates, setMediaStates] = useState<Record<string, File[]>>({});

  const [itemName, setItemName] = useState("");
  const [itemDesc, setItemDesc] = useState("");
  const [isItemNameValid, setIsItemNameValid] = useState(true);
  const [, setIsItemDescValid] = useState(true);

  const isUpdateDisabled = validationStates.some((isValid) => !isValid);
  const [showDragIcons, setShowDragIcons] = useState(true);
  const [userId, setUserId] = useState<string>("");

  const [newCardIndex, setNewCardIndex] = useState<number | null>(null);
  const [showEditAssigments, setShowEditAssigments] = useState<boolean>(false);
  const [
    customInspectionFormIdEditAssignments,
    setCustomInspectionFormIdEditAssignments,
  ] = useState<string>("");

  const [checked, setChecked] = useState<boolean>(false);

  const [pageInfo, setPageInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });

  const [dataInspectionTabList, setDataInspectionTabList] = useState(
    dataInspectionFormStatus,
  );

  const currentInspectionInfo = useSelector(
    (state: RootState) => state?.inspection?.currentInspectionInfo,
  );

  const screenPermission = useSelector(
    (state: RootState) => state?.screenPermission.value,
  );

  const { isRequestedGroupDetail } = useSelector(
    (state: RootState) => state.groupDetail,
  );

  const [
    allowCreateEditDeleteInspectionForm,
    setAllowCreateEditDeleteInspectionForm,
  ] = useState<boolean>(false);

  const {
    register,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const columns = [
    {
      key: "inspectionFormName",
      title: "inspectionTable.inspectionName",
      dataIndex: "inspectionFormName",
      sorted: true,
      style: {
        minWidth: isMobile() ? 220 : 515,
      },
      render: (value: string) => {
        return <span className="txt-pre-wrap">{value}</span>;
      },
      className: "txt-pre-wrap",
    },
    {
      key: "Assignment",
      title: "inspectionTable.assignment",
      dataIndex: "Assignment",
      sorted: true,
      style: {
        minWidth: isMobile() ? 178 : 198,
      },
      render: (value: number) => {
        const label =
          value === 1
            ? t("groupDetailTab.machineTab")
            : t("groupDetailTab.machineTabs");
        return <span className="txt-pre-wrap">{`${value} ${label}`}</span>;
      },
      className: "txt-pre-wrap",
    },

    {
      key: "currentStatus",
      title: "dialogReport.status",
      dataIndex: "currentStatus",
      style: {
        minWidth: 105,
      },
      render: (value: INSPECTION_TYPE) => {
        return (
          <div
            className={`status txt-nowrap ${
              value === INSPECTION_TYPE.PUBLISHED ? "active" : ""
            }`}
          >
            {t(`filterInspection.${INSPECTION_STATUS[value]}`)}
          </div>
        );
      },
    },
    {
      key: "lastStatusUpdatedAt",
      title: "inspectionTable.lastUpdate",
      dataIndex: "lastStatusUpdatedAt",
      sorted: true,
      align: "right",
      style: {
        minWidth: isMobile() ? 162 : 212,
      },
      render: (value: string) => {
        return (
          <span className="txt-nowrap">
            {format(new Date(value), userTimeFormat)}
          </span>
        );
      },
    },
    {
      key: "userPictureUrl",
      title: "",
      dataIndex: "userPictureUrl",
      render: (_: any, record: any) => {
        return (
          <>
            {!!record.userPictureUrl ? (
              record.userPictureUrl === INVALID_URL ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={record.userPictureUrl}
                  imgProps={{
                    onError: () => {
                      const newDataSource = dataSource?.map((item) => {
                        if (item.id === record.id) {
                          item.userPictureUrl = INVALID_URL;
                        }
                        return item;
                      });
                      setDataSource(newDataSource);
                    },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconUser />
              </Box>
            )}
          </>
        );
      },
      forceRender: true,
      className: "pr-4",
      tableHeadingClassName: "d-none-imp",
      width: 40,
    },
    {
      key: "updatedBy",
      title: "inspectionTable.updatedBy",
      dataIndex: "updatedBy",
      sorted: true,
      tableHeadingColspan: 2,
      className: "pl-0 txt-pre-wrap",
      style: {
        minWidth: isMobile() ? 120 : 185,
      },
    },
    {
      key: "id",
      title: "",
      dataIndex: "id",
      render: (_: string, record: any) => (
        <Button
          className="bnt-action-form"
          onClick={(e) => handleClick(e, record)}
        >
          <IconActionOverflow />
        </Button>
      ),
      className: "add",
      width: 48,
      align: "center",
    },
  ];

  const [userTimeFormat, setUserTimeFormat] =
    useState<string>("yyyy/MM/dd HH:mm");

  const [localeCode, setLocaleCode] = useState("");
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        let userId = response?.data?.data?.userId;
        let isoLocaleCode = response?.data?.data?.isoLocaleCode;

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setUserTimeFormat(timeFormat);
        setUserId(userId);
        setLocaleCode(isoLocaleCode);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleChangePage = (page: number) => {
    setPageInfo({
      ...pageInfo,
      page,
    });
  };

  const [groups, setGroups] = useState<GroupOption[]>([]);

  const handleGroupSelection = useCallback(
    async (selectedGroupIds: string[]) => {
      setLoading(true);
      try {
        const groupIdsParam = selectedGroupIds.join(",");

        const response = await apiGroupMachineTypeWebNew([groupIdsParam]);
        const fetchedMachines = response.data.data.machines;
        const isFormsAssigned = response.data.data.isFormsAssigned;

        const assignedMachineIds = new Set<string>();

        isFormsAssigned.forEach(
          (form: { customInspectionFormId: string; machines: string[] }) => {
            if (form.customInspectionFormId === inspectionFormId) {
              form.machines.forEach((machineId: string) => {
                const normalizedMachineId = machineId?.trim();
                if (normalizedMachineId) {
                  assignedMachineIds.add(normalizedMachineId);
                }
              });
            }
          },
        );

        const updatedMachines = fetchedMachines.map(
          (machine: { machineId: string; groupId: string }) => {
            const isAssigned = assignedMachineIds.has(machine.machineId);

            return {
              ...machine,
              isAssigned,
            };
          },
        );

        const initiallyAssignedMachines = updatedMachines
          .filter(
            (machine: {
              machineId: string;
              groupId: string;
              isAssigned: boolean;
            }) => machine.isAssigned,
          )
          .map(
            (machine: {
              machineId: string;
              groupId: string;
              isAssigned: boolean;
            }) => ({
              machineId: machine.machineId,
              groupId: machine.groupId,
              isAssigned: true,
            }),
          );

        setMachines(updatedMachines);
        setCheckedMachines(initiallyAssignedMachines);
        setAssignedMachines(initiallyAssignedMachines);
        setUnassignedMachines([]);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    [inspectionFormId],
  );

  const handleEditAssignmentsClick = useCallback(async () => {
    const items = await apiGetInspectionItemsNew(
      customInspectionFormIdEditAssignments,
    );

    setMachineTypeId(items.data.data.machineTypeId);
    setMachineIdForm(items.data.data.machineId);
    setInspectionFormId(items.data.data.inspectionFormId);
    setInspectionFormName(items.data.data.name);

    let formattedGroups: { value: string; label: string }[] = [];
    if (userId) {
      const response = await apiGetusergroups(userId);
      const userGroups = response.data.data;

      formattedGroups =
        userGroups?.userGroups?.map(
          (group: { groupId: any; groupName: any }) => ({
            value: group.groupId,
            label: group.groupName,
          }),
        ) || [];
    }

    setGroups(formattedGroups);

    setOpenAssignmentForm(true);

    handleClose();
  }, [customInspectionFormIdEditAssignments, groupId, userId]);

  const handleSort = (order: string | undefined, property: string) => {
    setOrder(order);
    setField(property);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBulkActionClick = (event: React.MouseEvent<HTMLElement>) => {
    setBulkAnchorEl(event.currentTarget);
  };

  const handleBulkActionClose = () => {
    setBulkAnchorEl(null);
  };

  const handleChangeInspectionStatus = (
    _: React.MouseEvent<HTMLElement>,
    inspectionStatus: INSPECTION_TYPE | "",
  ) => {
    setPageInfo({
      ...pageInfo,
      page: CURRENT_PAGE,
    });
    setInspectionStatus(inspectionStatus);
  };

  const handleFetchInspectionsForm = () => {
    setOpenInspectionsForm(true);
    setOpen(false);
  };

  const handleInspectionTemplateDownload = async (
    selection: InspectionTemplateSelection,
  ) => {
    if (!groupId) {
      return;
    }

    try {
      const blankTemplateResponse = await apiGetBlankInspectionFormTemplate(
        groupId,
        selection.machineId,
      );
      const blankTemplateUrl = resolveBlankInspectionTemplateFileUrl(
        blankTemplateResponse,
      );

      if (!blankTemplateUrl) {
        store.dispatch(setShowAlert(t("FailedtofetchtheExcelfile")));
        return;
      }

      const workbookBuffer = await buildInspectionBulkTemplateWorkbookBuffer(
        blankTemplateUrl,
        selection.templateData,
        {
          headers: {
            itemName: t("itemName"),
            description: t("explanation"),
            answerOption: t("AnswerOptions"),
            predefinedOptionColumns:
              buildInspectionTemplateOptionColumnHeaders(t),
          },
          answerOptions: {
            okOrAnomary: t("ANOMARY"),
            okAnomaryNotApplicable: t("ANOMARYNOTAPP"),
            commentFile: t("CommentFile"),
            predefinedComment: t("predefinedCommentSelection"),
          },
          meterItems: {
            serviceMeter: t("serviceMeterOrSMR"),
            odometer: t("odometer"),
          },
        },
      );
      const fileName = buildInspectionBulkTemplateFileName(
        getInspectionTemplateName(selection.selectedTemplate),
      );

      downloadInspectionBulkTemplateWorkbook(
        workbookBuffer as BlobPart,
        fileName,
      );
      store.dispatch(setShowAlert(t("FileDownloadedSuccessfully")));
    } catch (error) {
      store.dispatch(setShowAlert(t("ErrordownloadingExcelfile")));
      console.error("Error downloading inspection template:", error);
    }
  };

  const convertDataRow = useCallback(
    (data: InspectionType[]) => {
      return data.map((row: any) => ({
        ...row,
        id: row?.inspectionFormId,
        updatedBy:
          localeCode === "ja"
            ? `${row?.surname} ${row?.givenName}`
            : `${row?.givenName} ${row?.surname}`,
      }));
    },
    [localeCode],
  );

  const getGroupDetailInspectionFormStatus = useCallback((id: string) => {
    return apiGroupDetailInspectionFormStatus(id)
      .then((res) => {
        const data = res.data.data;
        setDataInspectionTabList((prev) =>
          prev.map((tab) => ({
            ...tab,
            count: data[tab.key],
          })),
        );
      })
      .catch(() => {});
  }, []);
  const initScrollToTop = useCallback(() => {
    if (isInit) {
      scrollToTop();
      setIsInit(false);
    }
  }, [isInit]);

  const getGroupDetailInspectionForms = useCallback(
    (id: string, params = {}) => {
      return apiGroupDetailInspectionForms(id, params)
        .then((res) => {
          const { data, meta } = res.data;
          const formatData = convertDataRow(data);

          setDataSource(formatData);
          handlePageInfo(meta, !data.length);

          if (!data.length) {
            initScrollToTop();
            resetIsContinuousLoading();
          }
        })
        .catch(() => {});
    },
    [convertDataRow, initScrollToTop],
  );

  useEffect(() => {
    if (!Object.keys(screenPermission).length) {
      return;
    }
    if (!!screenPermission?.allowCreateEditDeleteInspectionForm) {
      setAllowCreateEditDeleteInspectionForm(
        screenPermission.allowCreateEditDeleteInspectionForm,
      );
    } else {
      window.location.href = `/groups/${groupId}`;
    }
  }, [screenPermission]);

  useEffect(() => {
    if (groupId) {
      getGroupDetailInspectionFormStatus(groupId);
    }
  }, [requestData]);

  useEffect(() => {
    if (isRequestedGroupDetail && groupId) {
      setItem(StorageKey.IS_CONTINUOUS_LOADING, "true");
      getGroupDetailInspectionForms(groupId, {
        limit: LIMIT_PAGE,
        page: pageInfo.page,
        ...(inspectionStatus ? { currentStatus: inspectionStatus } : {}),
        ...(field && order ? { orderBys: [{ field, order }] } : {}),
      });
    }
  }, [
    pageInfo.page,
    field,
    order,
    inspectionStatus,
    requestData,
    isRequestedGroupDetail,
    localeCode,
  ]);

  useEffect(() => {
    return () => {
      resetIsContinuousLoading();
    };
  }, []);

  const handlePageInfo = (meta: any, hasData: boolean) => {
    if (meta.pageInfo.total) {
      if (hasData) {
        meta.pageInfo.page = meta.pageInfo.page - 1;
      }
    } else {
      setInspectionStatus("");
    }
    setPageInfo(meta.pageInfo);
  };

  const handleDeleteRow = debounce(() => {
    if (!targetRow) {
      return;
    }
    const { inspectionFormId, machineId } = targetRow;
    return apiGroupDetailInspectionFormDelete(
      `${groupId}`,
      machineId,
      inspectionFormId,
    )
      .then((res: any) => {
        store.dispatch(setShowAlert(res.data.meta.successMessage));
        setRequestData(new Date());
      })
      .finally(() => {
        setOpenFormDelete(false);
        setAnchorEl(null);
      });
  }, DEBOUNCE_TIME);

  const handleUpdateStatus = debounce((status: "ARCHIVED" | "PUBLISHED") => {
    if (!targetRow) {
      return;
    }
    const { inspectionFormId } = targetRow;

    return updateInspectionFormStatusApiCallNew(inspectionFormId, status)
      .then((res: any) => {
        store.dispatch(setShowAlert(res.data.meta.successMessage));
        setRequestData(new Date());
      })
      .catch((error) => {
        store.dispatch(
          setShowAlert("Failed to update inspection form status."),
        );
      })
      .finally(() => {
        if (status === "ARCHIVED") {
          setOpenFormArchive(false);
        } else if (status === "PUBLISHED") {
          setOpenFormPublish(false);
        }
        setAnchorEl(null);
      });
  }, DEBOUNCE_TIME);

  const handleEditRow = debounce(async () => {
    if (!targetRow) return;

    const { inspectionFormId } = targetRow;
    if (!groupId || !inspectionFormId) return;

    const inspectionFormName = getValues("inspectionFormName") || "";

    const updatedItems = await Promise.all(
      inspectionItems.map(async (item) => {
        const existingMedia = (item.inspectionItemMedias || []).filter(
          (media) =>
            !(removedMediaStates[item.inspectionItemId] || []).includes(
              media.filePath,
            ),
        );

        const newMedia = await handleConvertMedia(
          (mediaStates[item.inspectionItemId] || []).filter(
            (file) => file instanceof File,
          ),
        );

        const isHidden = hiddenItems.includes(item.inspectionItemId);
        return {
          customInspectionItemId: item.inspectionItemId,
          name: item.name,
          description: item.description,
          isRequired: item.isRequired,
          position: item.position,
          medias: [...existingMedia, ...newMedia],
          removedMedias: removedMediaStates[item.inspectionItemId] || [],
          resultType: item.resultType,
          status: isHidden ? "DELETED" : item.status,
          ...buildInspectionItemOptionsPayload(item),
        };
      }),
    );

    const deletedItemsPayload = deletedItems.map((item) => ({
      customInspectionItemId: item.inspectionItemId,
      name: item.name,
      description: item.description,
      isRequired: item.isRequired,
      position: item.position,
      medias: [],
      removedMedias: item.inspectionItemMedias.map((media) => media.filePath),
      status: "DELETED",
    }));

    const odometerItem = {
      customInspectionItemId: odoMeterID[0],
      name: odoMeterName[0],
      description: "",
      isRequired: odoMeterIsRequired,
      medias: [],
      removedMedias: [],
    };

    const payload = {
      formName: inspectionFormName,
      machineId: machineIdForm,
      items: [...updatedItems, ...deletedItemsPayload, odometerItem],
    };

    try {
      const response = await updateInspectionFormAndItemsApiCallWeb(
        inspectionFormId,
        payload,
      );

      store.dispatch(setShowAlert(t("inspectionformupdatedsuccess")));

      setDataSource((prevData = []) => {
        const filteredData = prevData.filter(
          (item) => item.id !== inspectionFormId,
        );
        return [...filteredData, { ...response.data, id: inspectionFormId }];
      });

      setRequestData(new Date());
    } catch (error) {
    } finally {
      setOpenFormName(false);
      setAnchorEl(null);
      setMediaStates({});
      setRemovedMediaStates({});
      setDeletedItemIds([]);
      setHiddenItems([]);
      setShowDragIcons(true);
      setDeletedItems([]);
      setInspectionItems([]);

      setOriginalInspectionItems([]);
    }
  }, DEBOUNCE_TIME);

  const handleShowCopyInspectionForm = () => {
    if (!!groupId && !!currentInspectionInfo) {
      store.dispatch(setMachineId(currentInspectionInfo.machineId));
      getCustomInspectionFormTemplates(
        groupId,
        currentInspectionInfo.machineId,
        currentInspectionInfo.inspectionFormId,
      ).then((db) => {
        store.dispatch(
          setInspectionsTemplate({
            ...db,
            inspectionItems: limitInspectionFormItems(
              db?.inspectionItems ?? [],
            ),
          }),
        );
        setOpenInspectionsForm(true);
      });
    }
  };

  interface GroupOption {
    value: string;
    label: string;
  }

  const [selectedGroup, setSelectedGroup] = useState("");
  const [, setMachineTypeId] = useState<string>("");
  const [machineIdForm, setMachineIdForm] = useState<string>("");

  const [inspectionFormName, setInspectionFormName] = useState<string>("");

  const handleClick = async (
    event: React.MouseEvent<HTMLElement>,
    target: any,
  ) => {
    setAnchorEl(event.currentTarget);

    setTargetRow({
      ...target,
      groupId,
    });

    store.dispatch(setCurrentInspectionInfo(target));

    if (target.inspectionFormId) {
      try {
        await fetchInspectionItems(
          target.inspectionFormId,
          setLoadingItems,
          setInspectionItems,
          setMediaStates,
        );
      } catch (error) {}
    }

    if (target.inspectionFormId) {
      try {
        const items = await apiGetInspectionItemsNew(target.inspectionFormId);
        setMachineTypeId(items.data.data.machineTypeId);
        setMachineIdForm(items.data.data.machineId);
        setInspectionFormId(items.data.data.inspectionFormId);
        setInspectionFormName(items.data.data.name);

        if (userId) {
          const response = await apiGetusergroups(userId);

          const userGroups = response.data.data;

          const formattedGroups =
            userGroups?.userGroups?.map(
              (group: { groupId: any; groupName: any }) => ({
                value: group.groupId,
                label: group.groupName,
              }),
            ) || [];

          setGroups(formattedGroups);

          if (formattedGroups.length > 0 && formattedGroups[0].value) {
            try {
            } catch (error) {
              console.error("Error Fetching Machines:", error);
            }
          } else {
            console.warn(
              "No valid groups available. Setting selectedGroup to empty.",
            );
          }
        } else {
          setGroups([]);
        }
      } catch (error) {
        console.error("Error fetching user groups:", error);
      }
    }
  };

  const [originalInspectionItems, setOriginalInspectionItems] = React.useState<
    InspectionItem[]
  >([]);
  useEffect(() => {
    if (showEditAssigments) {
      handleEditAssignmentsClick();
      setShowEditAssigments(false);
    }
  }, [showEditAssigments]);

  const fetchInspectionItems = async (
    customInspectionFormId: string,
    setLoadingItems: React.Dispatch<React.SetStateAction<boolean>>,
    setInspectionItems: React.Dispatch<React.SetStateAction<InspectionItem[]>>,
    setMediaStates: React.Dispatch<
      React.SetStateAction<Record<string, File[]>>
    >,
  ) => {
    try {
      setLoadingItems(true);

      const items = await getInspectionItemsNew(customInspectionFormId);

      const filteredItemss = items.filter(
        (item: { itemCode: string }) =>
          item.itemCode === "SERVICE_METER" || item.itemCode === "ODOMETER",
      );

      const serviceMeterName = filteredItemss
        .filter(
          (item: { itemCode: string }) => item.itemCode === "SERVICE_METER",
        )
        .map((item: { name: any }) => item.name);

      const odoMeterName = filteredItemss
        .filter((item: { itemCode: string }) => item.itemCode === "ODOMETER")
        .map((item: { name: any }) => item.name);

      const odometerID = filteredItemss
        .filter((item: { itemCode: string }) => item.itemCode === "ODOMETER")
        .map((item: { id: any }) => item.id);

      const odoMeterIsRequiredValue =
        filteredItemss.find(
          (item: { itemCode: string }) => item.itemCode === "ODOMETER",
        )?.isRequired || false;

      const itemsToProcess = items.slice(0, -2);

      const filteredItems = itemsToProcess
        .map(
          (item: {
            isRequired: any;
            id: any;
            name: any;
            description: any;
            position: any;
            inspectionItemMedias: any;
            resultType: any;
            options?: { name: string; position?: number }[];
          }) => ({
            inspectionItemId: item.id,
            name: item.name,
            description: item.description,
            isRequired: item.isRequired,
            position: item.position,
            inspectionItemMedias: item.inspectionItemMedias || [],
            resultType: item.resultType,
            options: item.options ?? [],
          }),
        )
        .slice(0, MAX_INSPECTION_FORM_ITEMS);

      setOriginalInspectionItems(filteredItems);

      const newMediaStates = filteredItems.reduce(
        (
          acc: Record<string, File[]>,
          item: {
            inspectionItemId: string | number;
            inspectionItemMedias: any;
          },
        ) => {
          acc[item.inspectionItemId] = (item.inspectionItemMedias || []).map(
            (media: any) => {
              const captureMediaAt = media.captureMediaAt;

              return {
                ...media,
                captureMediaAt: format(
                  new Date(captureMediaAt),
                  "yyyy-MM-dd HH:mm:ss",
                ),
              };
            },
          );
          return acc;
        },
        {},
      );

      setOdoMeterIsRequired(odoMeterIsRequiredValue);
      setServiceMeterName(serviceMeterName);
      setOdoMeterName(odoMeterName);
      setOdoMeterID(odometerID);

      setInspectionItems(filteredItems);
      setMediaStates(newMediaStates);
    } catch (error) {
      setInspectionItems([]);

      setServiceMeterName([]);
      setOdoMeterName([]);
      setOdoMeterIsRequired(false);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleUpdateItemName = useCallback(
    (index: number, newName: string) => {
      setInspectionItems((prevItems) => {
        const updatedItems = [...prevItems];

        const originalItem = originalInspectionItems[index];

        if (originalItem) {
          const isNameChanged = originalItem.name !== newName;

          if (isNameChanged) {
            setDeletedItems((prevDeleted) => {
              const alreadyDeleted = prevDeleted.some(
                (item) =>
                  item.inspectionItemId === originalItem.inspectionItemId,
              );
              if (alreadyDeleted) return prevDeleted;

              return [
                ...prevDeleted,
                {
                  ...originalItem,
                  status: "DELETED",
                },
              ];
            });

            updatedItems[index] = {
              ...updatedItems[index],
              name: newName,
              status: "CREATED",
            };
          } else {
            const { status, ...itemWithoutStatus } = updatedItems[index];
            updatedItems[index] = {
              ...itemWithoutStatus,
              name: newName,
            };

            setDeletedItems((prevDeleted) =>
              prevDeleted.filter(
                (item) =>
                  item.inspectionItemId !== originalItem.inspectionItemId,
              ),
            );
          }
        } else {
          updatedItems[index] = {
            ...updatedItems[index],
            name: newName,
          };
        }

        setValidationStates((prevStates) => {
          const updatedStates = [...prevStates];
          updatedStates[index] = newName.trim() !== "";
          return updatedStates;
        });

        return updatedItems;
      });
    },
    [originalInspectionItems],
  );

  const handleUpdateResultType = useCallback(
    (index: number, newType: string) => {
      setInspectionItems((prevItems) => {
        const updatedItems = [...prevItems];

        const originalItem = originalInspectionItems[index];

        if (originalItem) {
          const isResultTypeChanged = originalItem.resultType !== newType;

          if (isResultTypeChanged) {
            setDeletedItems((prevDeleted) => {
              const alreadyDeleted = prevDeleted.some(
                (item) =>
                  item.inspectionItemId === originalItem.inspectionItemId,
              );
              if (alreadyDeleted) return prevDeleted;

              return [
                ...prevDeleted,
                {
                  ...originalItem,
                  status: "DELETED",
                },
              ];
            });

            updatedItems[index] = {
              ...updatedItems[index],
              resultType: newType,
              options: updatedItems[index].options ?? [],
              status: "CREATED",
            };
          } else {
            const { status, ...itemWithoutStatus } = updatedItems[index];
            updatedItems[index] = {
              ...itemWithoutStatus,
              resultType: newType,
              options: updatedItems[index].options ?? [],
            };

            setDeletedItems((prevDeleted) =>
              prevDeleted.filter(
                (item) =>
                  item.inspectionItemId !== originalItem.inspectionItemId,
              ),
            );
          }
        } else {
          updatedItems[index] = {
            ...updatedItems[index],
            resultType: newType,
            options: updatedItems[index].options ?? [],
          };
        }

        return updatedItems;
      });
    },
    [originalInspectionItems],
  );

  const handleUpdateItemDescription = useCallback(
    (index: number, newDescription: string) => {
      setInspectionItems((prevItems) => {
        const updatedItems = [...prevItems];
        updatedItems[index] = {
          ...updatedItems[index],
          description: newDescription,
        };
        return updatedItems;
      });
    },
    [],
  );

  const handleUpdateItemOptions = useCallback(
    (index: number, options: NonNullable<InspectionItem["options"]>) => {
      setInspectionItems((prevItems) => {
        const updatedItems = [...prevItems];
        updatedItems[index] = {
          ...updatedItems[index],
          options,
        };
        return updatedItems;
      });
    },
    [],
  );

  const handleClickConfirm = () => {
    setOpenDialogConfirm(true);
    setConfirmDialog({
      classBtnSubmit: "btn-primary",
      typeDialog: DialogTypes.CONFIRM,
      title: "titleConfirm",
      content: "",
      btnSubmitText: "ok",
      onCancel() {
        setOpenDialogConfirm(false);
      },
      onSubmit() {
        setOpenDialogConfirm(false);
        reset({ inspectionFormName: "" });
        setOpenFormName(false);
        setMediaStates({});
        setRemovedMediaStates({});
        setDeletedItemIds([]);
        setHiddenItems([]);
        setValidationStatesDesc([]);
        setShowDragIcons(true);
        setDeletedItems([]);

        setOriginalInspectionItems([]);
      },
    });
  };

  const handleConvertMedia = async (files: File[]) => {
    const mediaArray = [];
    for await (const file of files) {
      if (file instanceof File) {
        const uploadedMedia = await handleUploadStore(
          UploadFileType.MACHINE_INSPECTIONS,
          file,
        );

        if (uploadedMedia) {
          mediaArray.push({
            fileName: uploadedMedia.name || file.name,
            filePath: uploadedMedia.filePath,
            mediaUrl: uploadedMedia.filePath,
            mimeType: uploadedMedia.type || file.type,

            captureMediaAt: new Date(file.lastModified).toISOString(),
          });
        }
      }
    }
    return mediaArray.filter((media) => media.fileName);
  };

  useEffect(() => {
    setValidationStates(inspectionItems.map((item) => item.name.trim() !== ""));
  }, [inspectionItems]);

  const handleToggleCard = (index: number) => {
    setOpenStates((prevStates) => {
      const updatedStates = [...prevStates];
      updatedStates[index] = !updatedStates[index];

      return updatedStates;
    });
  };

  useEffect(() => {
    setOpenStates(Array(inspectionItems.length).fill(false));
  }, [inspectionItems]);

  const uploadDescription = [
    `${t("textDesUploadFile.pleaseUploadAFileShowingTheInspectionPoints")}`,
    `${t("textDesUploadFile.fileFormatsThatCanBeUploadedAre")}`,
  ];

  const moveCard = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return;

    setInspectionItems((prevItems) => {
      const updatedItems = [...prevItems];
      const [draggedItem] = updatedItems.splice(dragIndex, 1);
      updatedItems.splice(hoverIndex, 0, draggedItem);

      const reorderedItems = updatedItems.map((item, index) => ({
        ...item,
        position: index,
      }));

      return reorderedItems;
    });
  };

  useEffect(() => {
    setValidationStatesDesc(
      inspectionItems.map(
        (item) =>
          item.description.trim() === "" ||
          (REGEX.commonNameFieldCharacterMuiltiLineRegex.test(
            item.description,
          ) &&
            REGEX.emojiRegex.test(item.description)),
      ),
    );
  }, [inspectionItems]);

  const [deletedItems, setDeletedItems] = useState<InspectionItem[]>([]);

  const handleDeleteCard = (id: string) => {
    setInspectionItems((prevItems) => {
      const itemToDelete = prevItems.find(
        (item) => item.inspectionItemId === id,
      );

      if (itemToDelete && itemToDelete.status !== "CREATED") {
        setDeletedItems((prevDeletedItems) => [
          ...prevDeletedItems,
          itemToDelete,
        ]);
      }

      return prevItems.filter((item) => item.inspectionItemId !== id);
    });

    store.dispatch(setShowAlert(t("alreadyDeleted")));
  };

  const handleItemNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (
      REGEX.commonNameFieldRegex.test(value) &&
      REGEX.emojiRegex.test(value)
    ) {
      setItemName(value);
      setIsItemNameValid(value.trim() !== "");
    } else {
      setIsItemNameValid(false);
    }
  };

  const handleItemDescChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (
      value.trim() === "" ||
      (REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) &&
        REGEX.emojiRegex.test(value))
    ) {
      setItemDesc(value);
      setIsItemDescValid(true);
    } else {
      setIsItemDescValid(false);
    }
  };

  const handleAddClickItem = () => {
    if (!itemName.trim()) {
      setIsItemNameValid(false);
      return;
    }
    if (
      targetRow &&
      groupId &&
      targetRow.machineId &&
      targetRow.inspectionFormId
    ) {
    } else {
    }

    setOpenAddNewItem(false);
    setOpenFormName(false);
    setItemName("");
    setItemDesc("");
    setIsItemNameValid(true);
    setIsItemDescValid(true);
    setOpenAddNewItem(false);
  };

  const handleValidationChange = (isValid: boolean, index: number) => {
    setValidationStates((prevStates) => {
      const updatedStates = [...prevStates];
      updatedStates[index] = isValid;
      return updatedStates;
    });
  };

  const handleValidationChangeDesc = (isValid: boolean, index: number) => {
    setValidationStatesDesc((prevStates) => {
      const updatedStates = [...prevStates];
      updatedStates[index] = isValid;
      return updatedStates;
    });
  };

  useEffect(() => {
    if (newCardIndex !== null) {
      setTimeout(() => setNewCardIndex(null), 0);
    }
  }, [newCardIndex]);

  const handleMediaUpload = (id: string, files: File[]) => {
    const existingFiles =
      mediaStates[id]?.map((file: any) => file.filePath) || [];
    const removedFiles = existingFiles.filter(
      (filePath: string) =>
        !files.find((file: any) => file.filePath === filePath),
    );

    if (removedFiles.length > 0) {
      setRemovedMediaStates((prev) => ({
        ...prev,
        [id]: [...(prev[id] || []), ...removedFiles],
      }));
    }

    setMediaStates((prevState) => ({
      ...prevState,
      [id]: files,
    }));

    const updatedPreviewData = {
      name: getValues("inspectionFormName") || "",
      customInspectionItems: inspectionItems.map((item) => ({
        ...item,
        customInspectionItemMedias: mediaStates[item.inspectionItemId] || [],
      })),
    };
    setDbPreview(updatedPreviewData);
  };

  const [openDialogPreview, setOpenDialogPreview] = useState(false);
  const [dbPreview, setDbPreview] = useState<any>(null);

  const handlePreView = () => {
    const previewData = {
      name: getValues("inspectionFormName") || "",
      customInspectionItems: inspectionItems.map((item) => ({
        name: item.name,
        description: item.description,
        resultType: item.resultType,
        options: item.options ?? [],
        customInspectionItemMedias: mediaStates[item.inspectionItemId] || [],
      })),
    };

    setDbPreview(previewData);
    setOpenDialogPreview(true);
  };

  interface CheckedMachine {
    machineId: string;
    groupId: string;
    isAssigned: boolean;
  }

  const [searchText, setSearchText] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState<AssignmentMachine[]>([]);
  const [checkedMachines, setCheckedMachines] = useState<CheckedMachine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<{
    machineId: string;
    groupId: string;
    isAssigned: boolean;
  } | null>(null);
  const [machineTypes, setMachineTypes] = useState<
    { value: string; typeName: string }[]
  >([]);
  const [selectedMachineType, setSelectedMachineType] =
    useState<string>("ALL_MACHINE_TYPES");
  const [assignedMachines, setAssignedMachines] = useState<CheckedMachine[]>(
    [],
  );
  const [unassignedMachines, setUnassignedMachines] = useState<
    { machineId: string; groupId: string; status: string }[]
  >([]);

  useEffect(() => {
    if (openAssignmentForm && groups.length > 0) {
      const providedGroupId = groupId;
      const selectedGroup = groups.find(
        (group) => group.value === providedGroupId,
      );

      if (selectedGroup) {
        setSelectedGroup(selectedGroup.value);
        handleGroupSelection([selectedGroup.value]);
      } else {
        setSelectedGroup(groups[0].value);
        handleGroupSelection([groups[0].value]);
      }
    }
  }, [openAssignmentForm, groups]);

  const handleCheckboxChange = (
    machineId: string,
    groupId: string,
    isAssigned: boolean,
  ) => {
    setCheckedMachines((prevChecked) => {
      const isChecked = prevChecked.some(
        (item) => item.machineId === machineId && item.groupId === groupId,
      );

      let updatedChecked;

      if (isChecked) {
        updatedChecked = prevChecked.filter(
          (item) => !(item.machineId === machineId && item.groupId === groupId),
        );

        if (isAssigned && assignedMachines.length > 0) {
          setUnassignedMachines((prevUnassigned) => {
            const newUnassigned = [
              ...prevUnassigned,
              { machineId, groupId, status: "ARCHIVED" },
            ];

            return newUnassigned;
          });

          setAssignedMachines((prevAssigned) => {
            const newAssigned = prevAssigned.filter(
              (item) =>
                !(item.machineId === machineId && item.groupId === groupId),
            );

            return newAssigned;
          });
        }
      } else {
        updatedChecked = [...prevChecked, { machineId, groupId, isAssigned }];

        setUnassignedMachines((prevUnassigned) => {
          const newUnassigned = prevUnassigned.filter(
            (item) =>
              !(item.machineId === machineId && item.groupId === groupId),
          );

          return newUnassigned;
        });

        if (isAssigned) {
          setAssignedMachines((prevAssigned) => {
            const newAssigned = [
              ...prevAssigned,
              { machineId, groupId, isAssigned },
            ];

            return newAssigned;
          });
        }
      }

      return updatedChecked;
    });
  };

  const filteredMachines = machines.filter((machine) => {
    const searchLower = searchText.replace(/\s/g, "").toLowerCase();
    const machineValues = [
      machine.machineName,
      machine.customMachineManufacturerName,
      machine.customTypeName,
      machine.serialNumber,
      machine.typeName,
      machine.modelAndType,
      machine.machineManufacturerName,
    ];

    const isSearchMatch = machineValues.some((value) =>
      (value?.toString().replace(/\s/g, "").toLowerCase() || "").includes(
        searchLower,
      ),
    );

    const isTypeMatch =
      selectedMachineType === "ALL_MACHINE_TYPES" ||
      getMachineTypeKey(machine) === selectedMachineType;

    return isSearchMatch && isTypeMatch;
  });

  const assignedFilteredMachines = assigned
    ? filteredMachines.filter((machine) => machine.isAssigned)
    : filteredMachines;

  useEffect(() => {
    const matchedMachine = machines.find(
      (machine) => machine.machineId === machineIdForm,
    );

    if (matchedMachine?.isAssigned) {
      setCheckedMachines((prevChecked) => {
        const isAlreadyChecked = prevChecked.some(
          (item) =>
            item.machineId === matchedMachine.machineId &&
            item.groupId === matchedMachine.groupId,
        );

        if (!isAlreadyChecked) {
          const updatedChecked = [
            ...prevChecked,
            {
              machineId: matchedMachine.machineId,
              groupId: matchedMachine.groupId,
              isAssigned: matchedMachine.isAssigned,
            },
          ];

          return updatedChecked;
        }

        return prevChecked;
      });
    }
  }, [machines, machineIdForm]);

  const publishAssignment = async () => {
    const inspectionFormId = customInspectionFormIdEditAssignments
      ? customInspectionFormIdEditAssignments
      : targetRow?.inspectionFormId;

    setCustomInspectionFormIdEditAssignments("");

    const alreadyAssignedMachineIds = new Set(
      assignedMachines.map((machine) => machine.machineId?.trim()),
    );
    const filteredMachines = checkedMachines.filter(
      (machine) =>
        !machine.isAssigned &&
        !alreadyAssignedMachineIds.has(machine.machineId?.trim()),
    );

    if (filteredMachines.length === 0 && unassignedMachines.length === 0) {
      console.warn("All selected machines are already assigned or unassigned.");
      store.dispatch(setShowAlert(t("formAction.AllMachinesAlreadyAssigned")));
      setOpenAssignmentForm(false);
      return;
    }

    try {
      const response = await apiGetInspectionItemsNew(inspectionFormId);
      const data = response.data.data;

      if (!data || !data.inspectionFormId || !data.name) {
        console.error("Invalid response data:", data);
        return;
      }

      const customInspectionFormId = data.inspectionFormId;
      const name = data.name;
      const payload = {
        data: [
          ...filteredMachines.map((machine) => ({
            machineId: machine.machineId,
            groupId: machine.groupId,
            name: name,
            customInspectionFormId: customInspectionFormId,
          })),

          ...unassignedMachines.map((machine) => ({
            machineId: machine.machineId,
            groupId: machine.groupId,
            name: name,
            customInspectionFormId: customInspectionFormId,
            status: "ARCHIVED",
          })),
        ],
      };

      await apiPostInspectionFormMultipleGroupsMachinesNew(payload);

      if (filteredMachines.length > 0) {
        store.dispatch(
          setShowAlert(
            t("formAction.Inspectionformsuccessfullyassignedtomachines"),
          ),
        );
      }

      if (unassignedMachines.length > 0) {
        store.dispatch(
          setShowAlert(
            t("formAction.Inspectionformsuccessfullyassignedtomachines"),
          ),
        );
      }

      setRequestData(new Date());
      setOpenAssignmentForm(false);
      setSearchText("");
      setSelectedGroup(groups[0]?.value || "");
      setAssigned(false);
      setGroups([]);
      setCheckedMachines([]);
      setMachineTypes([]);
      setSelectedMachineType("ALL_MACHINE_TYPES");
      setAssignedMachines([]);
      setUnassignedMachines([]);
    } catch (error) {
      console.error("Error publishing assignment:", error);
    }
  };

  useEffect(() => {
    if (checked) {
      setCheckedMachines((prevChecked) => {
        const newlyChecked = machines
          .filter((machine) => machine.isAssigned)
          .map((machine) => ({
            machineId: machine.machineId,
            groupId: machine.groupId,
            isAssigned: machine.isAssigned,
          }));

        const combined = [...prevChecked, ...newlyChecked];

        const uniqueByMachineAndGroupId = Array.from(
          new Map(
            combined.map((item) => [`${item.machineId}-${item.groupId}`, item]),
          ).values(),
        );

        return uniqueByMachineAndGroupId;
      });
    } else {
      setCheckedMachines(
        machines
          .filter((machine) => machine.isAssigned)
          .map((machine) => ({
            machineId: machine.machineId,
            groupId: machine.groupId,
            isAssigned: machine.isAssigned,
          })),
      );
    }
  }, [machines]);

  useEffect(() => {
    const uniqueMachineTypes = Array.from(
      new Map(
        machines.map((machine) => [
          getMachineTypeKey(machine),
          {
            value: getMachineTypeKey(machine),
            typeName: getMachineTypeDisplayName(machine),
          },
        ]),
      ).values(),
    );

    setMachineTypes(uniqueMachineTypes);
  }, [machines]);

  useEffect(() => {
    if (openAssignmentForm) {
      const assignedList = machines
        .filter((machine) => machine.isAssigned)
        .map((machine) => ({
          machineId: machine.machineId,
          groupId: machine.groupId,
          isAssigned: true,
        }));

      setAssignedMachines(assignedList);
    }
  }, [openAssignmentForm, machines]);

  const isAllSelected =
    filteredMachines.length > 0 &&
    filteredMachines.every((machine) =>
      checkedMachines.some(
        (item) =>
          item.machineId === machine.machineId &&
          item.groupId === machine.groupId,
      ),
    );

  const handleSelectAll = () => {
    setCheckedMachines((prevChecked) => {
      if (assignedMachines.length === 0) {
        const assignedList = machines
          .filter((m) => m.isAssigned)
          .map((m) => ({
            machineId: m.machineId,
            groupId: m.groupId,
            isAssigned: true,
          }));

        setAssignedMachines(assignedList);
      }

      if (isAllSelected) {
        setUnassignedMachines((prevUnassigned) => {
          const newUnassigned = [
            ...prevUnassigned,
            ...prevChecked
              .filter((item) => item.isAssigned)
              .map((item) => ({
                machineId: item.machineId,
                groupId: item.groupId,
                status: "ARCHIVED",
              })),
          ];

          return newUnassigned;
        });

        setAssignedMachines((prevAssigned) => {
          const updatedAssigned = prevAssigned.filter(
            (item) =>
              !prevChecked.some(
                (checkedItem) =>
                  checkedItem.machineId === item.machineId &&
                  checkedItem.groupId === item.groupId,
              ),
          );

          return updatedAssigned;
        });

        return [];
      } else {
        const newCheckedMachines = filteredMachines.map((machine) => ({
          machineId: machine.machineId,
          groupId: machine.groupId,
          isAssigned: machine.isAssigned,
        }));

        setUnassignedMachines((prevUnassigned) => {
          const updatedUnassigned = prevUnassigned.filter(
            (item) =>
              !filteredMachines.some(
                (machine) =>
                  machine.machineId === item.machineId &&
                  machine.groupId === item.groupId,
              ),
          );

          return updatedUnassigned;
        });

        setAssignedMachines((prevAssigned) => {
          const updatedAssigned = [
            ...prevAssigned,
            ...filteredMachines
              .filter((machine) => machine.isAssigned)
              .map((machine) => ({
                machineId: machine.machineId,
                groupId: machine.groupId,
                isAssigned: true,
              })),
          ];

          return updatedAssigned;
        });

        return newCheckedMachines;
      }
    });
  };

  return (
    <div className="inspection-tab">
      <div
        className={`flex group-button ${
          dataInspectionTabList[0].count
            ? "justify-content-between"
            : "justify-content-end"
        }`}
        style={{ marginTop: "24px" }}
      >
        <div className="toggle-btn-group">
          {!!dataInspectionTabList[0].count && (
            <ToggleButtonGroup
              className="filter-button"
              value={inspectionStatus}
              exclusive
            >
              {dataInspectionTabList.map((tab, index) => (
                <ToggleButton
                  key={index}
                  disabled={!tab.count}
                  value={tab.inspectionStatus}
                  className="flex-centered filter-tab"
                  onClick={handleChangeInspectionStatus}
                >
                  {typeof tab.label === "string" ? t(tab.label) : tab.label}
                  <span style={{ marginLeft: "8px" }}>({tab.count})</span>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </div>

        {allowCreateEditDeleteInspectionForm && (
          <div
            className="flex align-items-center"
            style={{ gap: "8px", flexWrap: "wrap" }}
          >
            <Button
              variant="contained"
              disableElevation
              className="btn-add-inspect"
              onClick={() => setOpen(true)}
            >
              <IconPlus />
              <span style={{ marginLeft: "8px" }}>{t("addInspection")}</span>
            </Button>

            <Button
              className="btn btn-no-border-dark-blue"
              startIcon={<MoreVert />}
              onClick={handleBulkActionClick}
              style={{
                height: "45px",
                fontSize: "14px",
              }}
              sx={{
                "& .MuiButton-startIcon": {
                  marginInlineEnd: "4px",
                },
              }}
            >
              {t("formAction.excelOptions")}
            </Button>

            <Menu
              anchorEl={bulkAnchorEl}
              open={openBulkAction}
              onClose={handleBulkActionClose}
            >
              <MenuItem
                onClick={() => {
                  setOpenBulkTemplateDownload(true);
                  handleBulkActionClose();
                }}
              >
                <ListItemIcon>
                  <DownloadOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("formAction.downloadexcel")}</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOpenBulkTemplateUpload(true);
                  handleBulkActionClose();
                }}
              >
                <ListItemIcon>
                  <UploadOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("upload")}</ListItemText>
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>

      <div className="inspection-table-container">
        <DataTable
          dataSource={dataSource}
          columns={columns}
          textEmptyData="noInspectionForms"
          onRequestSort={handleSort}
          didScrollTop={() => {
            initScrollToTop();
            resetIsContinuousLoading();
          }}
          loading={!dataSource}
        />
        <DataPagination
          isHidden={!dataSource?.length}
          className="mt-25 sp-p24"
          currentPage={pageInfo.page}
          limitPage={pageInfo.limit}
          totalPage={pageInfo.total}
          handleChangePage={(newPage) => {
            handleChangePage(newPage);
          }}
        />
      </div>

      <CreateInspectionForm
        open={open}
        setOpen={setOpen}
        clickSubmit={async () => handleFetchInspectionsForm()}
        isoLocaleCode={localeCode}
      />

      <CreateInspectionForm
        open={openBulkTemplateDownload}
        setOpen={setOpenBulkTemplateDownload}
        mode="download"
        onTemplateDownload={handleInspectionTemplateDownload}
        isoLocaleCode={localeCode}
      />

      <CreateInspectionForm
        open={openBulkTemplateUpload}
        setOpen={setOpenBulkTemplateUpload}
        mode="upload"
        clickSubmit={async () => handleFetchInspectionsForm()}
        isoLocaleCode={localeCode}
      />

      <InspectionFormDialog
        open={openInspectionsForm}
        setOpen={setOpenInspectionsForm}
        onRequestData={() => setRequestData(new Date())}
        setShowEditAssigments={setShowEditAssigments}
        setCustomInspectionFormIdEditAssignments={
          setCustomInspectionFormIdEditAssignments
        }
      />

      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={openFormAction}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {targetRow?.currentStatus === INSPECTION_TYPE.PUBLISHED && (
          <MenuItem
            onClick={() => {
              if (targetRow) {
                const { inspectionFormName } = targetRow;
                setValue("inspectionFormName", inspectionFormName);
              }
              setOpenFormName(true);
              handleClose();
            }}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText>{t("formAction.editSheet")}</ListItemText>
          </MenuItem>
        )}

        {targetRow?.currentStatus === INSPECTION_TYPE.DRAFT && (
          <MenuItem
            onClick={() => {
              if (targetRow) {
                const { inspectionFormName } = targetRow;
                setValue("inspectionFormName", inspectionFormName);
              }
              store.dispatch(setIsEditDraft(true));
              setOpenInspectionsForm(true);
              handleShowCopyInspectionForm();
              handleClose();
            }}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText>{t("formAction.edit")}</ListItemText>
          </MenuItem>
        )}

        {targetRow?.currentStatus === INSPECTION_TYPE.PUBLISHED && (
          <MenuItem
            onClick={async (event: React.MouseEvent<HTMLElement>) => {
              setChecked(true);
              setOpenAssignmentForm(true);

              const providedGroupId = groupId;
              const selectedGroup = groups.find(
                (group) => group.value === providedGroupId,
              );

              if (selectedGroup) {
                setSelectedGroup(selectedGroup.value);
                handleGroupSelection([selectedGroup.value]);
              } else if (groups.length > 0) {
                setSelectedGroup(groups[0].value);
                handleGroupSelection([groups[0].value]);
              }

              handleClose();
            }}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText>{t("formAction.editAssignments")}</ListItemText>
          </MenuItem>
        )}

        {targetRow?.currentStatus === INSPECTION_TYPE.DRAFT && (
          <MenuItem
            onClick={() => {
              setOpenFormDelete(true);
              handleClose();
            }}
          >
            <ListItemIcon>
              <IconDeleteRow />
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" className="txt-red fs-16">
                  {t("formAction.delete")}
                </Typography>
              }
            />
          </MenuItem>
        )}

        {targetRow?.currentStatus === INSPECTION_TYPE.PUBLISHED && (
          <MenuItem
            onClick={() => {
              setOpenFormArchive(true);
              handleClose();
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "auto",
                marginRight: "-5px",
                marginLeft: "5px",
                "& svg": {
                  fontSize: "2.5rem",
                },
              }}
            >
              <IconDelete />
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" className="txt-red fs-16">
                  {t("formAction.delete")}
                </Typography>
              }
            />
          </MenuItem>
        )}

        {targetRow?.currentStatus === INSPECTION_TYPE.ARCHIVED && (
          <MenuItem
            onClick={() => {
              setOpenFormPublish(true);
              handleClose();
            }}
          >
            <ListItemIcon>
              <IconPublishedRow />
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" className="txt-green fs-16">
                  {t("formAction.publish")}
                </Typography>
              }
            />
          </MenuItem>
        )}
      </Menu>

      <DataDialog
        disableBackdropClick={true}
        title={t("titleConfirm")}
        open={openFormDeleteAssignment}
        setOpen={setOpenFormDeleteAssignment}
      >
        <div className="form-confirm-delete">
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDeleteAssignment(false)}
              className="btn-cancel"
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary"
              onClick={() => {
                setOpenAssignmentForm(false);
                setSearchText("");
                setAssigned(false);
                setSelectedGroup(groups[0]?.value || "");
                setGroups([]);
                setCheckedMachines([]);
                setChecked(false);
                setMachineTypes([]);
                setSelectedMachineType("ALL_MACHINE_TYPES");
                setAssignedMachines([]);
                setUnassignedMachines([]);
                setOpenFormDeleteAssignment(false);
              }}
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("formDelete.title")}
        open={openFormDelete}
        setOpen={setOpenFormDelete}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("formDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDelete(false)}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button className="btn-delete" onClick={handleDeleteRow}>
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("formArchive.title")}
        open={openFormArchive}
        setOpen={setOpenFormArchive}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("formArchive.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormArchive(false)}
              className="btn btn-no-border"
              style={{ marginTop: "4px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn-delete"
              onClick={() => handleUpdateStatus("ARCHIVED")}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("formPublish.title")}
        open={openFormPublish}
        setOpen={setOpenFormPublish}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("formPublish.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormPublish(false)}
              className="btn-cancel"
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary btn-save ml-10"
              onClick={() => handleUpdateStatus("PUBLISHED")}
            >
              {t("formAction.publish")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={openFormName}
        setOpen={setOpenFormName}
        title={t("formEdit.title")}
        className="dialog-inspection-form"
        action={
          <div className="flex header-actions">
            <Button
              className="btn-no-border-preview"
              onClick={() => handlePreView()}
            >
              <VisibilityOutlined
                className="icon-visibility"
                style={{ color: "#180e9c", fontSize: "16px" }}
              />

              <span
                className="preview-text ml-8"
                style={{
                  textTransform: "none",
                  color: "#180e9c",
                  fontSize: "14px",
                }}
              >
                {t("preview")}
              </span>
            </Button>
          </div>
        }
      >
        <div className="form-edit-name">
          <div className="wrapper-text-field mt-8">
            <TextField
              id={
                errors?.inspectionFormName?.type
                  ? "outlined-error"
                  : "outlined-controlled"
              }
              {...register("inspectionFormName", {
                validate: {
                  hasEmoji: (value) => REGEX.emojiRegex.test(value),
                  hasInvalidChar: (value) =>
                    REGEX.commonNameFieldRegex.test(value),
                  required: (value) => !!value.trim(),
                  maxGraphemes: (value) => {
                    const segmenter = new Intl.Segmenter(undefined, {
                      granularity: "grapheme",
                    });
                    return [...segmenter.segment(value)].length <= 50;
                  },
                },

                onChange: (e) => {
                  const safeValue = safeSliceGraphemes(e.target.value, 50);
                  setValue("inspectionFormName", safeValue, {
                    shouldValidate: true,
                  });
                },

                onBlur: (e) =>
                  setValue("inspectionFormName", e.target.value?.trim()),
              })}
              className="input-field"
              error={
                !!errors.inspectionFormName?.type &&
                errors?.inspectionFormName?.type !== "maxGraphemes"
              }
              label={`${t("inspectionTable.inspectionName")}*`}
            />
            {errors?.inspectionFormName?.type && <IconInputError />}
          </div>

          {errors?.inspectionFormName?.type && (
            <p className="txt-red fs-12 mt-0 mb-0 ml-15">
              {errors?.inspectionFormName?.type === "required" &&
                t("messages.require")}
              {errors?.inspectionFormName?.type === "hasInvalidChar" &&
                t("messages.validCharacters")}
              {errors?.inspectionFormName?.type === "hasEmoji" &&
                t("messages.validCharacters")}
            </p>
          )}

          <div
            className="inspection-items mt-16"
            style={{ marginBottom: "0px" }}
          >
            {loadingItems ? (
              <p></p>
            ) : inspectionItems.length > 0 ? (
              <ul className="inspection-items-list">
                {inspectionItems
                  .filter(
                    (item) => !hiddenItems.includes(item.inspectionItemId),
                  )
                  .map((item, index) => (
                    <UpdateFormCardUI
                      key={item.inspectionItemId}
                      id={item.inspectionItemId}
                      index={index}
                      moveCard={moveCard}
                      inspectionItemName={item.name}
                      inspectionItemNameErrors={null}
                      inspectionItemDesErrors={null}
                      prevCardName={item.name}
                      prevCardDescription={item.description}
                      errors={errors}
                      onUpdateItemName={(newName) =>
                        handleUpdateItemName(index, newName)
                      }
                      onUpdateItemDescription={(newDescription) =>
                        handleUpdateItemDescription(index, newDescription)
                      }
                      mediaFiles={mediaStates[item.inspectionItemId] || []}
                      onMediaUpload={(files) =>
                        handleMediaUpload(item.inspectionItemId, files)
                      }
                      inspectionItemId={item.inspectionItemId}
                      setMediaStates={setMediaStates}
                      removedMediaStates={setRemovedMediaStates}
                      isOpen={openStates[index]}
                      onToggle={() => handleToggleCard(index)}
                      onDeleteCard={handleDeleteCard}
                      onValidationChange={handleValidationChange}
                      onValidationChangeDesc={handleValidationChangeDesc}
                      showDragIcon={showDragIcons}
                      focusOnName={newCardIndex === index}
                      resultType={item.resultType || ResultType.OK_OR_ANOMARY}
                      onResultTypeChange={(newType) =>
                        handleUpdateResultType(index, newType)
                      }
                      itemOptions={
                        item.options?.map((option) => ({
                          customInspectionItemsOptionsId:
                            option.customInspectionItemsOptionsId,
                          name: option.name,
                          position: option.position ?? 0,
                        })) ?? []
                      }
                      onOptionsChange={(options) =>
                        handleUpdateItemOptions(index, options)
                      }
                    />
                  ))}
              </ul>
            ) : (
              <p>{t("noItemsFound")}</p>
            )}
          </div>

          <div className="flex-centered mt-24">
            <FormActionButton
              className="btn-secondary"
              disabled={inspectionItems.length >= MAX_INSPECTION_FORM_ITEMS}
              onClick={() => {
                if (inspectionItems.length >= MAX_INSPECTION_FORM_ITEMS) {
                  return;
                }

                const generateUniqueInspectionItemId = (existingIds: {
                  has: (arg0: string) => any;
                }) => {
                  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
                  let id;
                  do {
                    id = "";
                    for (let i = 0; i < 26; i++) {
                      id += characters.charAt(
                        Math.floor(Math.random() * characters.length),
                      );
                    }
                  } while (existingIds.has(id));
                  return id;
                };

                const existingIds = new Set(
                  inspectionItems.map((item) => item.inspectionItemId),
                );

                const newCard = {
                  inspectionItemId: generateUniqueInspectionItemId(existingIds),
                  name: "",
                  description: "",
                  isRequired: true,
                  position: inspectionItems.length,
                  status: "CREATED",
                  inspectionItemMedias: [],
                  resultType: ResultType.OK_OR_ANOMARY,
                  options: [],
                };

                setOpenStates((prevStates) => {
                  const updatedStates = [...prevStates, true];

                  return updatedStates;
                });
                setInspectionItems([...inspectionItems, newCard]);
                setValidationStates((prevStates) => [...prevStates, false]);
                setValidationStatesDesc((prevStates) => [...prevStates, true]);

                setOpenStates((prevStates) => [...prevStates, true]);

                setNewCardIndex(inspectionItems.length);
              }}
            >
              <Add className="icon-add" />
              {t("addInspectionItem")}
            </FormActionButton>
          </div>

          <div
            className="flex-centered mt-24"
            style={{ marginBottom: "0px" }}
          ></div>

          <ul className="cards bg-gray" style={{ borderRadius: "0" }}>
            <li className="card-item">
              <div
                className={`card-item-title flex-centered justify-content-between "have-switch"`}
                style={{ paddingBottom: "8px", marginBottom: "8px" }}
              >
                <span
                  className="txt-card txt-gray-2"
                  style={{ fontSize: "16px", color: "#6B7280" }}
                >
                  {serviceMeterName.length > 0
                    ? `${serviceMeterName.join(", ")}*`
                    : t("smr")}
                </span>
              </div>
            </li>
            <li className="card-item">
              <div
                className={`card-item-title flex-centered justify-content-between "have-switch"`}
              >
                <span
                  className="txt-card"
                  style={{ fontSize: "16px", color: "#374151" }}
                >
                  {odoMeterName.length > 0
                    ? `${odoMeterName.join(", ")}${
                        odoMeterIsRequired ? "*" : ""
                      }`
                    : t("odometer")}
                </span>
                <div className="d-flex align-items-center">
                  <span
                    className="fs-14 txt-gray-3 mr-15"
                    style={{ color: "#6B7280" }}
                  >
                    {t("required")}
                  </span>
                  <Switch
                    className={`btn-switch ${
                      odoMeterIsRequired ? "checked" : ""
                    }`}
                    checked={odoMeterIsRequired}
                    onChange={(event) =>
                      setOdoMeterIsRequired(event.target.checked)
                    }
                  />
                </div>
              </div>
            </li>
          </ul>

          <div
            className="flex justify-content-end btn-group-footer mt-8"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              paddingTop: "16px",

              zIndex: 10,
              marginRight: "0px",
            }}
          >
            <Button
              onClick={handleClickConfirm}
              className="btn btn-no-border"
              style={{ width: "auto", marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              disabled={
                !!errors.inspectionFormName?.type ||
                validationStates.some((isValid) => !isValid) ||
                inspectionItems.some((item) => !item.name.trim()) ||
                inspectionItems.some(
                  (item) => !hasValidPredefinedComments(item),
                ) ||
                isUpdateDisabled ||
                validationStatesDesc.some((isValid) => !isValid)
              }
              className="btn btn-primary"
              onClick={handleEditRow}
              style={{ width: "auto" }}
            >
              {t("formAction.update")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("formDelete.titleNew")}
        open={openAddNewItem}
        setOpen={setOpenAddNewItem}
        groupId={groupId}
        machineId={targetRow?.machineId}
        formId={targetRow?.inspectionFormId}
      >
        <div className="form-confirm-delete">
          <div className="card-item-content d-block">
            <div
              className="wrapper-text-field mt-8"
              style={{ position: "relative" }}
            >
              <TextField
                label={`${t("itemName")}*`}
                className="input-field"
                inputProps={{ maxLength: 100 }}
                value={itemName}
                onChange={handleItemNameChange}
                error={!isItemNameValid}
              />
              {!isItemNameValid && (
                <div
                  style={{
                    position: "absolute",
                    right: "5px",
                    top: "40%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconInputError />
                </div>
              )}
              <span className="fs-12 ml-15 txt-gray-1">
                &#42;{t("required")}
              </span>
            </div>

            <div className="wrapper-text-field mt-8">
              <TextField
                multiline
                maxRows={3}
                className="input-field input-text-area"
                label={t("explanation")}
                inputProps={{ maxLength: 150 }}
                value={itemDesc}
                onChange={handleItemDescChange}
              />
            </div>

            <div className="mt-16">
              <MediaUpload
                setMedias={(files: File[]) => {}}
                typeFile={TYPE_FILE_UPLOAD.image}
                uploadDescription={uploadDescription}
              />
            </div>
          </div>

          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setItemName("");
                setItemDesc("");
                setIsItemNameValid(true);
                setIsItemDescValid(true);
                setOpenAddNewItem(false);
              }}
              className="btn-cancel"
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary btn-save ml-10"
              disabled={!itemName.trim()}
              onClick={handleAddClickItem}
            >
              {t("formAction.add")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <ConfirmDialog
        open={openDialogConfirm}
        setOpen={setOpenDialogConfirm}
        {...confirmDialog}
      />

      <DataDialog
        disableBackdropClick={true}
        open={openAssignmentForm}
        setOpen={setOpenAssignmentForm}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("inspecFormAss")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            >
              {inspectionFormName}
            </span>
          </div>
        }
        className="dialog-inspection-form"
      >
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ gap: "15px", marginBottom: "20px" }}
        >
          <span className="fs-14 txt-gray-3" style={{ color: "#6B7280" }}>
            {t("formAction.assigned")}
          </span>

          <Switch
            className={`btn-switch ${assigned ? "checked" : ""}`}
            checked={assigned}
            onChange={(event) => setAssigned(event.target.checked)}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <TextField
            select
            label={t("formAction.Groups")}
            variant="outlined"
            fullWidth
            value={selectedGroup}
            onChange={(event) => {
              const selectedValue = event.target.value;
              setSelectedGroup(selectedValue);

              if (selectedValue === "ALL_GROUPS") {
                handleGroupSelection(groups.map((group) => group.value));
              } else {
                handleGroupSelection([selectedValue]);
              }

              setMachineTypes([]);
              setAssignedMachines([]);
              setUnassignedMachines([]);
              setSelectedMachineType("ALL_MACHINE_TYPES");
              setSearchText("");
              setAssigned(false);
            }}
            style={{ flex: 1, minWidth: "0px" }}
          >
            <MenuItem key="all_groups" value="ALL_GROUPS">
              {t("all")}
            </MenuItem>

            {groups.length > 0 ? (
              groups.map((group, index) => (
                <MenuItem key={index} value={group.value}>
                  {group.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">{t("formAction.NoGroupsAvailable")}</MenuItem>
            )}
          </TextField>
        </div>

        <div style={{ marginTop: "20px" }}>
          <TextField
            select
            label={t("machineInfo.machineType")}
            variant="outlined"
            fullWidth
            value={selectedMachineType}
            onChange={(event) => setSelectedMachineType(event.target.value)}
            style={{ flex: 1, minWidth: "0px" }}
          >
            <MenuItem key="all_machine_types" value="ALL_MACHINE_TYPES">
              {t("all")}
            </MenuItem>

            {machineTypes.length > 0 ? (
              machineTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.typeName}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="">
                {t("formAction.NoMachineTypesAvailable")}
              </MenuItem>
            )}
          </TextField>
        </div>

        <div style={{ marginTop: "20px" }}>
          <Divider></Divider>
        </div>

        <div
          style={{
            position: "relative",
            flex: 1,
            minWidth: "0px",
            marginTop: "20px",
          }}
        >
          <Search
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "black",
            }}
            size={18}
          />

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("Customermachinenumber")}
            style={{
              width: "100%",
              height: "55px",
              padding: "10px 40px",
              borderRadius: "10px",
              border: "1px solid #ccc",
              backgroundColor: "#f5f5f5",
              fontSize: "16px",
              outline: "none",
              boxShadow: "none",
              color: "black",
            }}
          />

          {searchText && (
            <X
              tabIndex={0}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "black",
                cursor: "pointer",
              }}
              size={18}
              onClick={() => setSearchText("")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setSearchText("");
              }}
            />
          )}
        </div>

        <div
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            padding: "10px 0",
            marginTop: "10px",
          }}
        >
          {!loading && (
            <>
              {filteredMachines.length > 0 && !assigned && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                    padding: "0 10px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "normal",
                      color: "#333",
                      margin: 0,
                    }}
                  ></p>

                  <span
                    onClick={handleSelectAll}
                    style={{
                      fontSize: "14px",
                      color: "#140a9a",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    {isAllSelected
                      ? t("formAction.deselectAll")
                      : t("selectAll")}
                  </span>
                </div>
              )}

              {assignedFilteredMachines.length > 0 && (
                <div>
                  <Divider />
                  {assignedFilteredMachines.map((machine, index) => (
                    <React.Fragment key={index}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px",
                          backgroundColor: "#fff",
                          marginBottom: "0px",
                        }}
                      >
                        <img
                          src={machine.pictureUrl}
                          alt={machine.machineName}
                          style={{
                            width: "50px",
                            height: "50px",
                            marginRight: "12px",
                            border: "1px solid #ccc",
                            borderRadius: "6px",
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#333",
                            }}
                          >
                            {machine.machineName}
                          </div>
                          <div style={{ fontSize: "16px", color: "#666" }}>
                            {machine.modelAndType} • {machine.serialNumber} •{" "}
                            {machine.machineManufacturerName}
                          </div>
                          <div style={{ fontSize: "16px", color: "#666" }}>
                            {machine.groupName}
                          </div>
                        </div>

                        <Checkbox
                          checked={checkedMachines.some(
                            (item) =>
                              item.machineId === machine.machineId &&
                              item.groupId === machine.groupId,
                          )}
                          onChange={() => {
                            handleCheckboxChange(
                              machine.machineId,
                              machine.groupId,
                              machine.isAssigned,
                            );
                          }}
                          sx={{
                            color: "#333",
                            "&.Mui-checked": {
                              color: "#140a9a",
                            },
                          }}
                        />
                      </div>

                      <Divider />
                    </React.Fragment>
                  ))}
                </div>
              )}

              {assignedFilteredMachines.length === 0 &&
                filteredMachines.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#888",
                      fontSize: "18px",
                      padding: "50px",
                    }}
                  >
                    <IconBox />
                    <p style={{ marginTop: 8 }}>
                      {searchText
                        ? t("noregmach")
                        : machines.length === 0
                          ? t("noregmach")
                          : t("noregmach")}
                    </p>
                  </div>
                )}
            </>
          )}
        </div>

        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            style={{ width: "auto" }}
            className="btn btn-no-border"
            onClick={() => {
              setOpenFormDeleteAssignment(true);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            style={{ width: "auto" }}
            onClick={() => {
              publishAssignment();
            }}
            disabled={
              checkedMachines.length === 0 &&
              assignedMachines.length === 0 &&
              unassignedMachines.length === 0
            }
          >
            {t("formAction.publish")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={openConAssignmentForm}
        setOpen={setOpenConAssignmentForm}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("inspecFormAss")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            ></span>
          </div>
        }
      >
        <div
          className="warning flex justify-content-start"
          style={{ marginTop: 0, marginBottom: "10px" }}
        >
          <IconWarningFormEdit />
          <span style={{ marginLeft: 12, flex: 1 }} className="txt-black-1">
            {t("formtypecontent")}
          </span>
        </div>

        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            style={{ width: "auto" }}
            className="btn btn-no-border"
            onClick={() => {
              setOpenConAssignmentForm(false);
              setSelectedMachine(null);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            style={{ width: "auto" }}
            onClick={() => {
              if (selectedMachine) {
                handleCheckboxChange(
                  selectedMachine.machineId,
                  selectedMachine.groupId,
                  selectedMachine.isAssigned,
                );
              }
              setOpenConAssignmentForm(false);
              setSelectedMachine(null);
            }}
          >
            {t("formAction.add")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        open={openConAssignmentFormMin}
        setOpen={setOpenConAssignmentFormMin}
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("inspecFormAss")}</span>
            <span
              className="fs-16 txt-gray-3"
              style={{ color: "#6B7280", marginTop: "-4px" }}
            ></span>
          </div>
        }
      >
        <div
          className="warning flex justify-content-start"
          style={{ marginTop: 0, marginBottom: "10px" }}
        >
          <IconWarningFormEdit />
          <span style={{ marginLeft: 12, flex: 1 }} className="txt-black-1">
            {t("formtypecontentmin")}
          </span>
        </div>

        <div
          className="flex justify-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-primary"
            style={{ width: "auto" }}
            onClick={() => {
              setOpenConAssignmentFormMin(false);
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <PreviewInspectionDialogEdit
        open={openDialogPreview}
        setOpen={setOpenDialogPreview}
        data={dbPreview}
        mediaStates={mediaStates}
        serviceMeterName={serviceMeterName}
        odoMeterName={odoMeterName}
        odoMeterIsRequired={odoMeterIsRequired}
      />
    </div>
  );
}
